import React, { useEffect } from 'react'
import Script from 'next/script'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { useAuth } from '@dustin-web/microsite-components/src/auth'

const PROD_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  '/webanalytics/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','DigitalData','GTM-7S3Z');`

const TEST_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  '/webanalytics/gtm.js?id='+i+dl+ '&gtm_auth=DG1zKoZGNUM4YaSk4W5i1Q&gtm_preview=env-2488&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
  })(window,document,'script','DigitalData','GTM-7S3Z');`

const DelayedGTM = ({ serverEnv }: { serverEnv: string }) => {
  useEffect(() => {
    const autoLoadDuration = 5000
    const eventList = [
      'click',
      'keydown',
      'mousemove',
      'wheel',
      'touchmove',
      'touchstart',
      'touchend',
    ]

    const loadGTMScript = () => {
      const script = document.createElement('script')
      script.id = 'init-delayed-gtm'
      script.textContent = serverEnv === 'production' ? PROD_SCRIPT : TEST_SCRIPT
      document.head.appendChild(script)
    }

    const autoLoadTimeout = setTimeout(() => {
      loadGTMScript()
      removeListeners()
    }, autoLoadDuration)

    const triggerScripts = () => {
      loadGTMScript()
      clearTimeout(autoLoadTimeout)
      removeListeners()
    }

    eventList.forEach(event => {
      window.addEventListener(event, triggerScripts, { passive: true })
    })

    const removeListeners = () => {
      eventList.forEach(event => {
        window.removeEventListener(event, triggerScripts)
      })
    }

    return () => {
      clearTimeout(autoLoadTimeout)
      removeListeners()
    }
  }, [])

  return null
}

// We should move this to _document once we're fully standalone
const RenderGtmScripts = () => {
  const { userData } = useAuth()
  const { isProxied, serverEnv } = useSiteContext()

  const { pageUriWithoutQuery } = useCleanRouter()

  useEffect(() => {
    window.DigitalData?.push({ originalLocation: document.URL })
  }, [pageUriWithoutQuery])

  useEffect(() => {
    DigitalData.loginUser(userData?.contact?.customerErp ?? null)
  }, [userData?.contact?.customerErp])

  if (!isProxied) {
    return null
  }

  const digitalDataObj = {
    PageInstanceId: null,
    Cart: null,
    Transaction: null,
    User: null,
    PageInfo: null,
    Products: [],
  }

  return (
    <>
      <Script id="init-digital-data" strategy="afterInteractive">
        {`
            if (!DigitalData) {
              var DigitalData = [];
            }

            var digitalDataObj = ${JSON.stringify(digitalDataObj)};
            DigitalData.push(digitalDataObj);
          `}
      </Script>
      <DelayedGTM serverEnv={serverEnv} />
      <noscript>
        <iframe
          src="//www.googletagmanager.com/ns.html?id=GTM-7S3Z"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}></iframe>
      </noscript>
    </>
  )
}

export default RenderGtmScripts
